import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  UserPlus, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  FolderOpen
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = isAdmin() ? [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/coaches', icon: Users, label: 'Coaches' },
  ] : [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/coachees', icon: Users, label: 'Coachees' },
    { path: '/coachees/new', icon: UserPlus, label: 'Nuevo Coachee' },
    { path: '/documents', icon: FolderOpen, label: 'Documentos' },
  ];

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  return (
    <div className="min-h-screen app-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-card rounded-none border-x-0 border-t-0 px-4 py-3 flex items-center justify-between">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        <h1 className="text-heading text-xl text-blue-900">Sesiona</h1>
        <Avatar className="w-8 h-8">
          <AvatarFallback className="bg-blue-900 text-white text-xs">
            {getInitials(user?.nombre)}
          </AvatarFallback>
        </Avatar>
      </header>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-72 z-50 glass-card rounded-none border-y-0 border-l-0 
                   transform transition-transform duration-300 ease-out
                   lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full p-6">
          {/* Logo */}
          <div className="flex items-center justify-between mb-8">
            <Link to={isAdmin() ? '/admin' : '/dashboard'} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg p-1">
                <img 
                  src="https://sesiona.online/logo_Sesiona.png" 
                  alt="Sesiona" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-heading text-2xl text-blue-900">Sesiona</span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* User Info */}
          <div className="glass-card p-4 mb-6 flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-blue-900 text-white font-semibold">
                {getInitials(user?.nombre)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 truncate">{user?.nombre}</p>
              <p className="text-sm text-slate-500 capitalize">{user?.rol}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            <p className="label-uppercase px-4 mb-3">Menú</p>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                              (item.path !== '/dashboard' && item.path !== '/admin' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="pt-4 border-t border-slate-200">
            <button 
              onClick={handleLogout}
              className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
